import React from 'react';
import { Award, Heart, Users, Clock, MapPin } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Hero Section */}
      <section className="pt-20 bg-background">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-6xl font-bold mb-4 text-charcoal">Our Story</h1>
          <div className="w-32 h-1 bg-olive mx-auto mb-6"></div>
          <p style={{ fontSize: '20px', color: '#4a7043', fontStyle: 'italic' }}>La Nostra Storia</p>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Timeline Introduction */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-charcoal mb-6">Over Four Decades of Tradition</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              What began as a dream in 1978 has become a beloved part of Peterborough. Explore the story of The Pizza House and the Fasulo family.
            </p>
          </div>

          {/* Story Timeline */}
          <div className="space-y-12">
            {/* 1978 - The Beginning */}
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/3">
                <div className="bg-olive text-white rounded-full w-20 h-20 flex items-center justify-center text-2xl font-bold mx-auto">
                  1978
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-charcoal mb-4">The Dream Begins</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    In 1978, Rinaldo Fasulo's vision came to life as The Pizza House opened its doors in Peterborough's historic Cowgate. 
                    What started as a simple dream of bringing authentic Italian flavors to the community would grow into something 
                    much bigger - a beloved local institution that would serve generations of families.
                  </p>
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="/The-Dream-Begins.jpg?v=2024-2" 
                      alt="The Dream Begins - Historic Cowgate 1978" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 1980 - Taking Control */}
            <div className="flex flex-col md:flex-row-reverse items-center gap-8">
              <div className="md:w-1/3">
                <div className="bg-olive text-white rounded-full w-20 h-20 flex items-center justify-center text-2xl font-bold mx-auto">
                  1980
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-charcoal mb-4">Building a Reputation</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    By 1980, Rinaldo had fully taken the reins of The Pizza House, pouring his heart and soul into every dish. 
                    His hands-on approach in the kitchen and genuine warmth with customers quickly established The Pizza House 
                    as the go-to destination for authentic Italian cuisine and genuine hospitality in Peterborough.
                  </p>
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="/Rinaldo-in-kitchen-1980s.jpg?v=2024-2" 
                      alt="Rinaldo Fasulo in the kitchen during the 1980s" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* The Growth Years */}
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/3">
                <div className="bg-olive text-white rounded-full w-20 h-20 flex items-center justify-center text-xl font-bold mx-auto">
                  80s-90s
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-charcoal mb-4">Empire Expansion</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    The success of The Pizza House laid the foundation for Rinaldo's expanding hospitality empire. 
                    Over the next three decades, he launched celebrated venues including Fifth Avenue, 
                    Break for the Border, and Halo. Yet despite this remarkable growth, The Pizza House 
                    always held a special place in his heart - the original dream that started it all.
                  </p>
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="/Restaurant-empire-collag.jpg?v=2024-2" 
                      alt="Restaurant empire collage showing the growth of Rinaldo's hospitality ventures" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* The Pep Era */}
            <div className="flex flex-col md:flex-row-reverse items-center gap-8">
              <div className="md:w-1/3">
                <div className="bg-olive text-white rounded-full w-20 h-20 flex items-center justify-center text-lg font-bold mx-auto text-center leading-tight">
                  Family<br/>Legacy
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-charcoal mb-4">Pep's Touch</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    Rinaldo's brother, Pep Fasulo, became the heart and soul of The Pizza House, 
                    greeting generations of customers with his infectious smile and legendary hospitality. 
                    His famous half-pizza lunch deals and genuine care for every guest turned The Pizza House 
                    into more than just a restaurant - it became a home away from home for the community.
                  </p>
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="/Pep-serving-customers.jpg?v=2024-2" 
                      alt="Pep Fasulo serving customers with his legendary hospitality" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Challenges & Resilience */}
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/3">
                <div className="bg-olive text-white rounded-full w-20 h-20 flex items-center justify-center text-lg font-bold mx-auto text-center leading-tight">
                  2016
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-charcoal mb-4">Weathering the Storm</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    In 2016, The Pizza House adapted to changing times and later rose to the challenges of the COVID-19 pandemic. Through it all, its spirit endured, a testament to the legacy of Pep Fasulo and the loyalty of those who had come to see it as more than just a restaurant.
                  </p>
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="/Closed-restaurant-renovation.jpg?v=2024-2" 
                      alt="The Pizza House during renovation and challenging times" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 2019 - The Comeback */}
            <div className="flex flex-col md:flex-row-reverse items-center gap-8">
              <div className="md:w-1/3">
                <div className="bg-olive text-white rounded-full w-20 h-20 flex items-center justify-center text-2xl font-bold mx-auto">
                  2019
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-charcoal mb-4">Back and Better Than Ever</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    In 2019, The Pizza House reopened following a major renovation and expansion. 
                    With a refreshed interior and updated kitchen, Rinaldo returned to the heart of 
                    the restaurant, getting hands-on once again.
                  </p>
                  <blockquote className="mb-6 pl-4 border-l-4 border-primary italic text-olive">
                    "I've started again from scratch," - Rinaldo Fasulo
                  </blockquote>
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="/Renovated restaurant interior.jpg?v=2024-2" 
                      alt="The renovated Pizza House interior after the 2019 expansion" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Today */}
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/3">
                <div className="bg-olive text-white rounded-full w-20 h-20 flex items-center justify-center text-xl font-bold mx-auto">
                  Today
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-charcoal mb-4">Passion & Tradition</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    Today, The Pizza House stands stronger than ever, proudly holding a five-star food hygiene rating 
                    while continuing to serve Peterborough with the same passion and tradition that started it all. 
                    Rinaldo's return to the kitchen brings renewed energy, as he personally oversees every dish 
                    and reconnects with the community that has supported The Pizza House for over four decades.
                  </p>
                  <div className="w-full h-48 bg-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src="/Modern-Pizza-House-today.jpg?v=2024-2" 
                      alt="The modern Pizza House today, continuing the legacy" 
                      className="w-full h-full object-contain md:object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-charcoal mb-6">More Than Just Pizza</h2>
            <p className="text-xl text-gray-600">It's about family, history, and a love for authentic Italian food.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-background rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-charcoal mb-4">Family</h3>
              <p className="text-gray-600">
                Four decades of family tradition, where every meal is prepared with love and 
                served with the warmth that only a family business can provide.
              </p>
            </div>

            <div className="text-center p-8 bg-background rounded-lg shadow-md">
              <div className="w-16 h-16 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-charcoal mb-4">History</h3>
              <p className="text-gray-600">
                From 1978 to today, we've been part of Peterborough's story, creating memories 
                and serving generations of families in our beloved Cowgate location.
              </p>
            </div>

            <div className="text-center p-8 bg-background rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-charcoal mb-4">Tradition</h3>
              <p className="text-gray-600">
                Authentic Italian recipes, quality ingredients, and traditional cooking methods 
                that have made us a trusted name in Italian cuisine.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="bg-olive text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8 text-white">Recognition & Awards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex items-center justify-center space-x-4">
              <Award size={48} className="text-yellow-300" />
              <div className="text-left">
                <h3 className="text-2xl font-bold">5-Star Food Hygiene Rating</h3>
                <p className="text-lg opacity-90">Certified by Food Standards Agency</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-4">
              <Users size={48} className="text-yellow-300" />
              <div className="text-left">
                <h3 className="text-2xl font-bold">Community Favorite</h3>
                <p className="text-lg opacity-90">Serving Peterborough since 1978</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="bg-charcoal text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8 text-white">Visit Us Today</h2>
          <div className="flex items-center justify-center space-x-4 mb-6">
            <MapPin size={24} className="text-primary" />
            <p className="text-xl">The Pizza House, 3 Fortune Buildings, Cowgate, Peterborough, Cambridgeshire, PE1 1LR</p>
          </div>
          <p className="text-lg mb-8 opacity-90">
            Come experience the warmth of family tradition and taste the authentic Italian flavors 
            that have made us a Peterborough institution for over four decades.
          </p>
          <button 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-primary text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Where to Find Us
          </button>
        </div>
      </section>
    </div>
  );
}
